import { Auth0Client } from "@auth0/auth0-spa-js";
import { jwtDecode } from "jwt-decode";
import { createApiIdentityPlugin } from "zudoku/plugins";
import { queryClient } from "../src/contexts/QueryClientProvider";
import { authorizedEntitiesQueryKeys } from "../src/hooks/useEntityQueries";
import {
  ApiIdentityService,
  EntityIdentity,
} from "../src/services/apiIdentityService";
import { getEntities } from "../src/services/entityService";

/*
 * The claims requested for the entity are the claims that are always required for the API.
 */
export const claims = ["PLC_*", "EAM_*", "APPSTORE_*", "WEBHOOKS_*"];

const auth0Domain =
  process.env.ZUDOKU_PUBLIC_AUTH0_DOMAIN || "login.vpctest.com";

/**
 * Encodes a user name and key to the basic authentication format.
 * @param userName The user name to encode
 * @param key The key to encode
 * @returns The basic authentication encoded string
 */
function basicEncode(userName: string, key: string): string {
  return btoa(userName + ":" + key);
}

const auth0 = new Auth0Client({
  domain: auth0Domain,
  clientId: process.env.ZUDOKU_PUBLIC_AUTH0_CLIENT_ID || "",
});

async function getUserEmail(accessToken: string) {
  const decodedToken = jwtDecode(accessToken);
  return (decodedToken as Record<string, string>)?.email ?? "Logged in user";
}

/**
 * Callback function for authorizing requests for an entity identity
 * @param entity
 */
const authorizeRequest =
  (entity: EntityIdentity) => async (request: Request) => {
    const baseScopes = `openid profile email`;
    const wildcardScopes = claims.map((claim) => `platform:entity/*::${claim}`);
    const entityScopes = claims.map(
      (claim) => `platform:entity/${entity.id}::${claim}`
    );

    const scope = [baseScopes, ...wildcardScopes, ...entityScopes].join(" ");

    console.log("scopes for token request", { scopes: scope });

    try {
      const tokenResponse = await auth0.getTokenSilently({
        authorizationParams: {
          scope,
        },
        detailedResponse: true,
      });
      if (tokenResponse) {
        console.debug("token response", tokenResponse);
        const { access_token } = tokenResponse;
        const jwtPayload = jwtDecode(access_token);
        console.debug("jwt payload", jwtPayload);

        request.headers.set("Authorization", `Bearer ${access_token}`);
        return request;
      } else {
        console.warn("No token response from Auth0");
      }
    } catch (error) {
      console.warn("Error authorizing request:", error);
      throw new Error(
        `Cannot authorize request with identity "Entity: ${entity.label}"`,
        { cause: error }
      );
    }
    throw new Error(
      `Cannot authorize request with identity "Entity: ${entity.label}"`
    );
  };

/**
 * Registers an API identity for each locally stored API key.
 */
export const apiApiKeyIdentitiesPlugin = createApiIdentityPlugin({
  getIdentities: async () => {
    const apiIdentityService = new ApiIdentityService(window.localStorage);

    // API key identities are for everyone
    const apiKeys = await apiIdentityService.getApiKeys();
    return apiKeys.map((apiKey) => ({
      id: `api-key-${apiKey.id}`,
      label: `🔑 ${apiKey.label}`,
      authorizeRequest: async (request: Request) => {
        const token = apiKey.key;
        if (token) {
          if (apiKey.userName) {
            request.headers.set(
              "Authorization",
              `Basic ${basicEncode(apiKey.userName, token)}`
            );
          } else {
            request.headers.set("Authorization", `Token ${token}`);
          }
        }
        return request;
      },
    }));
  },
});

/**
 * Registers an API identity for each entity the user has access to.
 */
export const apiEntityIdentitiesPlugin = createApiIdentityPlugin({
  getIdentities: async (context) => {
    const apiIdentityService = new ApiIdentityService(window.localStorage);

    const accessToken = (await context.authentication?.getAccessToken()) ?? "";

    const userEmail = await getUserEmail(accessToken);

    const authorizedEntities = await queryClient
      .fetchQuery({
        queryKey: authorizedEntitiesQueryKeys.list(),
        queryFn: () => getEntities(accessToken),
      })
      .catch((error) => {
        console.error("Error fetching authorized entities:", error);
        return { data: [], meta: { hasWildcard: false } };
      });

    // For users with wildcard access, we need to include the manual entity identities
    if (authorizedEntities.meta.hasWildcard) {
      const entities = await apiIdentityService.getEntities();

      // Entity identities
      return entities.map((entity) => ({
        id: `entity-${entity.id}`,
        label: `👤 ${userEmail} (${entity.label})`,
        authorizeRequest: authorizeRequest(entity),
      }));
    } else {
      // For users without wildcard access, we need to include the authorized entities
      return authorizedEntities.data.map((entity) => ({
        id: `entity-${entity.id}`,
        label: `👤 ${userEmail} (${entity.attributes.name})`,
        authorizeRequest: authorizeRequest({
          id: `entity-${entity.id}`,
          label: `👤 ${userEmail} (${entity.attributes.name})`,
        }),
      }));
    }
  },
});
